/**
 * TenantBillingBanner
 *
 * Banner fino exibido no topo do app para tenants no gateway Uaizy (integracao_v1).
 * Mostra status de trial (com progressão de cor conforme urgência) e past_due
 * dentro do período de dunning.
 *
 * @version 2.0.0
 *  - Corrigida sobreposição pelo sidebar fixo (z-index + padding-left responsivo).
 *  - Progressão de cor: azul (>5d) → âmbar (3-5d) → vermelho (≤2d) no trial.
 *  - Redesign premium: chip de ícone, título+subtexto, pill de contagem, CTA sólido.
 */

import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTenantBillingSelfOverview } from "@/hooks/tenant/useTenantPlansV2";

/** Calcula dias restantes (arredondado para cima) entre agora e uma ISO date. */
function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return null;
  return Math.max(0, Math.ceil(diffMs / 86_400_000));
}

/** Formata ISO em dd/mm/aaaa (pt-BR). */
function formatDateBR(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR");
}

/**
 * Tom visual do banner conforme urgência.
 * Usa tokens semânticos + tokens auxiliares (yellow e destructive já usados
 * no projeto) para manter compatibilidade com dark mode.
 */
type Tone = "info" | "warning" | "danger";

interface ToneClasses {
  wrapper: string;
  chip: string;
  icon: string;
  pill: string;
  cta: string;
}

const TONE_MAP: Record<Tone, ToneClasses> = {
  info: {
    wrapper:
      "bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-primary/30",
    chip: "bg-primary/15 text-primary ring-1 ring-primary/25",
    icon: "text-primary",
    pill: "bg-primary/15 text-primary ring-1 ring-primary/25",
    cta: "bg-primary text-primary-foreground hover:bg-primary/90",
  },
  warning: {
    wrapper:
      "bg-gradient-to-r from-yellow-500/15 via-yellow-500/10 to-yellow-500/15 border-b border-yellow-500/40",
    chip:
      "bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 ring-1 ring-yellow-500/40",
    icon: "text-yellow-700 dark:text-yellow-300",
    pill:
      "bg-yellow-500/20 text-yellow-800 dark:text-yellow-200 ring-1 ring-yellow-500/40",
    cta: "bg-yellow-600 text-white hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-400 dark:text-slate-900",
  },
  danger: {
    wrapper:
      "bg-gradient-to-r from-destructive/15 via-destructive/10 to-destructive/15 border-b border-destructive/40",
    chip: "bg-destructive/20 text-destructive ring-1 ring-destructive/40",
    icon: "text-destructive",
    pill: "bg-destructive/20 text-destructive ring-1 ring-destructive/40",
    cta: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  },
};

/** Deriva o tom conforme dias restantes do trial. */
function toneForTrialDays(left: number | null): Tone {
  if (left === null) return "info";
  if (left <= 2) return "danger";
  if (left <= 5) return "warning";
  return "info";
}

export function TenantBillingBanner() {
  const navigate = useNavigate();
  const { data: overview } = useTenantBillingSelfOverview();

  if (!overview || !overview.has_binding) return null;
  if (overview.gateway_slug && overview.gateway_slug !== "uaizy") return null;

  // -----------------------------------------------------------------------
  // Estado TRIAL — progressão de cor conforme dias restantes
  // -----------------------------------------------------------------------
  if (overview.status === "trial") {
    const left = daysUntil(overview.trial_ends_at);
    const endsAt = formatDateBR(overview.trial_ends_at);
    const tone = toneForTrialDays(left);
    const classes = TONE_MAP[tone];
    const isUrgent = tone === "danger";
    const daysLabel =
      left === null
        ? "—"
        : left === 0
        ? "Último dia"
        : `${left} dia${left === 1 ? "" : "s"}`;

    return (
      <BannerShell tone={tone} classes={classes}>
        <div className="flex items-center gap-3 min-w-0">
          {/* Chip do ícone */}
          <div
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full shrink-0",
              classes.chip,
              isUrgent && "animate-pulse",
            )}
          >
            {isUrgent ? (
              <Zap className={cn("h-4 w-4", classes.icon)} />
            ) : (
              <Clock className={cn("h-4 w-4", classes.icon)} />
            )}
          </div>

          {/* Título + subtexto */}
          <div className="flex flex-col leading-tight min-w-0">
            <span className="text-sm font-semibold text-foreground truncate">
              {isUrgent
                ? "Seu período de teste está acabando"
                : "Período de teste ativo"}
            </span>
            <span className="text-xs text-muted-foreground truncate">
              {endsAt ? <>Termina em {endsAt}</> : "Escolha um plano para continuar"}
            </span>
          </div>

          {/* Pill de contagem */}
          <span
            className={cn(
              "hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0",
              classes.pill,
            )}
          >
            {daysLabel}
          </span>
        </div>

        <Button
          size="sm"
          className={cn("h-8 gap-1.5 shrink-0 shadow-sm", classes.cta)}
          onClick={() => navigate("/tenant/plans")}
        >
          {isUrgent ? "Assinar agora" : "Ver planos"}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </BannerShell>
    );
  }

  // -----------------------------------------------------------------------
  // Estado PAST_DUE (dentro do dunning) — sempre vermelho urgente
  // -----------------------------------------------------------------------
  if (
    overview.status === "past_due" &&
    (overview.dunning_days_left ?? 0) > 0
  ) {
    const classes = TONE_MAP.danger;
    const daysLeft = overview.dunning_days_left ?? 0;

    return (
      <BannerShell tone="danger" classes={classes}>
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full shrink-0 animate-pulse",
              classes.chip,
            )}
          >
            <AlertTriangle className={cn("h-4 w-4", classes.icon)} />
          </div>

          <div className="flex flex-col leading-tight min-w-0">
            <span className="text-sm font-semibold text-foreground truncate">
              Pagamento em atraso
            </span>
            <span className="text-xs text-muted-foreground truncate">
              Acesso liberado por mais {daysLeft} dia
              {daysLeft === 1 ? "" : "s"}
            </span>
          </div>

          <span
            className={cn(
              "hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0",
              classes.pill,
            )}
          >
            {daysLeft} dia{daysLeft === 1 ? "" : "s"}
          </span>
        </div>

        <Button
          size="sm"
          className={cn("h-8 gap-1.5 shrink-0 shadow-sm", classes.cta)}
          onClick={() => navigate("/tenant/plans?past_due=1")}
        >
          Regularizar
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </BannerShell>
    );
  }

  return null;
}

/**
 * Shell comum do banner:
 *  - `relative z-0` garante que o banner fique ATRÁS do sidebar fixo (z-40),
 *    permitindo que o menu lateral se sobreponha visualmente.
 *  - Conteúdo principal centralizado horizontalmente na página; CTA
 *    posicionado absoluto à direita para não deslocar o centro.
 */
function BannerShell({
  tone: _tone,
  classes,
  children,
}: {
  tone: Tone;
  classes: ToneClasses;
  children: React.ReactNode;
}) {
  // children esperado: [conteúdo principal, botão CTA]
  const items = Array.isArray(children) ? children : [children];
  const content = items[0];
  const cta = items[1];

  return (
    <div
      className={cn(
        "relative z-0 w-full px-4 py-2",
        "flex items-center justify-center",
        "backdrop-blur-sm",
        classes.wrapper,
      )}
    >
      <div className="flex items-center justify-center gap-3 min-w-0">
        {content}
      </div>
      {cta ? (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          {cta}
        </div>
      ) : null}
    </div>
  );
}
